import { useNavigate } from "@tanstack/react-router";
import { Lock, Eye, EyeOff, Save, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { toast } from "@/shared/components";
import { supabase } from "@/shared/lib/supabase";
import { Input, Label } from "@/shared/components";
import { AuthTemplate } from "@modootoday/ui-widgets/templates";

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const passwordMatch =
    password && passwordConfirm && password === passwordConfirm;
  const passwordMismatch = passwordConfirm && password !== passwordConfirm;
  const canSubmit = password && passwordMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      toast.success("비밀번호가 성공적으로 변경되었습니다");
      navigate({ to: "/auth/login" });
    } catch (err: any) {
      toast.error(err.message || "비밀번호 변경에 실패했습니다");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthTemplate
      title="새 비밀번호 설정"
      subtitle="새로운 비밀번호를 입력하여 계정 보안을 유지하세요."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label className="mb-1.5 block">새 비밀번호</Label>
          <Input
            type={showPw ? "text" : "password"}
            placeholder="8자 이상 입력"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            leadingIcon={<Lock className="size-4" />}
            trailingIcon={
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                aria-label={showPw ? "비밀번호 숨기기" : "비밀번호 표시"}
                className="text-text-tertiary hover:text-text-primary"
              >
                {showPw ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </button>
            }
            autoFocus
          />
        </div>

        <div>
          <Label className="mb-1.5 block">비밀번호 확인</Label>
          <Input
            type={showPw ? "text" : "password"}
            placeholder="비밀번호를 다시 입력하세요"
            value={passwordConfirm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPasswordConfirm(e.target.value)
            }
            leadingIcon={<Lock className="size-4" />}
            className={passwordMismatch ? "border-signal-danger" : ""}
          />
          {passwordMismatch && (
            <p className="mt-1 text-[11px] text-signal-danger">
              비밀번호가 일치하지 않습니다
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={!canSubmit || loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent py-2.5 text-[13px] font-semibold text-text-inverse transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {loading ? (
            <span className="size-4 animate-spin rounded-full border-2 border-text-inverse/30 border-t-primary-foreground" />
          ) : (
            <Save className="size-4" />
          )}
          {loading ? "변경 중..." : "비밀번호 변경"}
        </button>
      </form>

      <div className="mt-2 text-center">
        <button
          onClick={() => navigate({ to: "/auth/login" })}
          className="inline-flex items-center gap-1.5 text-[12px] text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft className="size-3" /> 로그인으로 돌아가기
        </button>
      </div>
    </AuthTemplate>
  );
}
